import {useObserver} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {FlatList, StyleSheet, View} from 'react-native';
import {PRIMARY_BACKGROUND_COLOR} from "../../Config/Colors";
import Logger from "../../Domain/Logger/Logger";
import ScreenTitle from "../Shared/ScreenTitle";
import PowerProxy from "../../Domain/Models/PowerProxy";
import PowerProxyDataGridItem from "../Shared/PowerProxyDataGridItem";
import {powerProxyRepository} from "../../Domain/Repositories/PowerProxyRepository";
import ProxyListHeader from "../Shared/ProxyListHeader";
import PowerProxyService from "../../Domain/Services/PowerProxyService";
import EditPowerProxyModal from "../Shared/Modals/EditPowerProxyModal";
import {
    CONNECTION_STATUS_TIMINGS_MILLISECONDS,
    POWER_PROXY_USE_DUMMY_DATA,
} from "../../Config/Parameters";
import useRefreshConnectionStatus from "../../Domain/Hooks/useRefreshConnectionStatus";
import ListFooter from "../Shared/ListFooter";
import GenericNotificationModal from "../Shared/Modals/GenericNotificationModal";
import {userRepository} from "../../Domain/Repositories/UserRepository";
import {
    FETCHING_FAILED_TITLE,
    FETCHING_SUCCESS_TITLE,
    IS_NOT_LOGGED_IN,
    POWER_PROXY_DASHBOARD_TITLE,
    PRIMARY_TITLE,
} from "../../Config/Translations";

const logger = Logger.Create('PowerProxySubView');

interface PowerProxySubViewProps
{
    /**
     * Callback when the menu was pressed.
     */
    onMenuButtonPressed?: () => void;

    /**
     * Callback when the login was pressed.
     */
    onLoginPressed?: () => void;
}

let timer: NodeJS.Timeout | undefined;

/**
 * Renders the Power Proxy Sub View.
 */
export default function PowerProxySubView({
    onMenuButtonPressed,
    onLoginPressed,
}: PowerProxySubViewProps): JSX.Element
{
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedProxy, setSelectedProxy] = useState<PowerProxy>(powerProxyRepository.dummyPowerProxy);
    const [showIndicator, setShowIndicator] = useState(false);
    const [indicatorTitle, setIndicatorTitle] = useState(FETCHING_SUCCESS_TITLE);
    const [mounted, setMounted] = useState(false);
    const [showNotification, setShowNotification] = useState(false);

    const { refreshConnection, stopRefresh } = useRefreshConnectionStatus(async () =>
    {
        await refreshList();
    });
    
    const refreshList = async (): Promise<void> =>
    {
        setIndicatorTitle(FETCHING_SUCCESS_TITLE);

        // Timer interval elapsed. Check for all power proxies.
        const success = await PowerProxyService.getProxies(POWER_PROXY_USE_DUMMY_DATA);

        if (!success)
        {
            setIndicatorTitle(FETCHING_FAILED_TITLE);
        }

        updateIndicator();
    }

    const updateIndicator = (): void =>
    {
        if (timer !== undefined)
        {
            clearTimeout(timer);
            timer = undefined;
        }

        setShowIndicator(true);
        timer = setTimeout(() =>
        {
            setShowIndicator(false);
        }, CONNECTION_STATUS_TIMINGS_MILLISECONDS.indicatorDuration);        
    }
    
    useEffect(() =>
    {
        let isMounted = true;
        setMounted(true);
        
        if (isMounted)
        {
            refreshConnection();
        }

        return () => {
            isMounted = false
            setMounted(false);
            stopRefresh();
        };
    }, []);
    
    const onEditButtonPressed = (proxy: PowerProxy): void =>
    {
        if (!userRepository.isLoggedIn())
        {
            setShowNotification(true);
            return;
        }
        
        setSelectedProxy(proxy);
        setShowEditModal(true);
    }

    const onCloseModalPressed = (shouldUpdate: boolean): void =>
    {
        logger.info(`Close modal was selected. Should make another request?: ${shouldUpdate}`);

        if (shouldUpdate)
        {
            refreshList();
        }

        setShowEditModal(false);
    }

    const render = (powerProxy: PowerProxy[]): JSX.Element =>
    {
        return (
            <FlatList
                bounces
                horizontal={false}
                data={powerProxy}
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                renderItem={({ item: entry, index }): JSX.Element => (
                    <PowerProxyDataGridItem
                        index={index}
                        powerProxy={entry}
                        onEditButtonSelected={onEditButtonPressed}
                    />
                )}
                style={styles.flatList}
                keyExtractor={(entry, index): string => `${entry.id}-${index}`}
            />
        )
    }
    
    const onMenuPressed = (): void =>
    {
        if (mounted && onMenuButtonPressed !== undefined)
        {
            onMenuButtonPressed();
        }
    }
    
    return useObserver(() => (
        <View style={styles.container}>
            <View style={{flex: 0.02}} />

            <ScreenTitle
                title={PRIMARY_TITLE}
                showLogin
                onMenuPressed={onMenuPressed}
                underline
                onLoginPressed={onLoginPressed}
            />

            <ScreenTitle
                title={POWER_PROXY_DASHBOARD_TITLE}
                showMenu={false}
                showLoading={showIndicator}
                loadingTitle={indicatorTitle}
            />

            <ProxyListHeader/>

            {render(powerProxyRepository.powerProxies)}

            <ListFooter
                showFooterOnly={true}
                onRefreshPressed={refreshList}
            />
            
            <EditPowerProxyModal
                show={showEditModal}
                selectedProxy={selectedProxy}
                onCloseButtonSelected={onCloseModalPressed}
            />

            <GenericNotificationModal
                show={showNotification}
                onCloseButtonSelected={() => setShowNotification(false)}
                message={IS_NOT_LOGGED_IN}
            />
        </View>
    ));
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: PRIMARY_BACKGROUND_COLOR,
        width: "100%",
        height: "100%",
        alignItems: 'center',
        justifyContent: 'center',
        alignContent: 'center',
    },
    flatList: {
        flex: 0.9,
        width: "100%",
        backgroundColor: PRIMARY_BACKGROUND_COLOR,
    },
});
